import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Icon } from './ui/Icon.jsx';
import { useImageUpload } from '../hooks/useImageUpload.js';

// ─── STATUS COLOURS ───────────────────────────────────────────────────────────
const PHASE = {
  compressing: { bar: 'var(--sedra-sage-ink)', label_en: 'Compressing…',     label_ar: 'ضغط الصورة…'   },
  uploading:   { bar: 'var(--sedra-forest-soft)', label_en: 'Uploading…',        label_ar: 'جاري الرفع…'   },
  done:        { bar: 'var(--status-success)', label_en: 'Uploaded',        label_ar: 'تم الرفع'    },
  error:       { bar: 'var(--status-danger)', label_en: 'Upload failed',     label_ar: 'فشل الرفع'     },
};

// ─── MIME TYPE GUARD ─────────────────────────────────────────────────────────
const ACCEPT = ['image/jpeg','image/jpg','image/png','image/webp',
                'image/gif','image/avif','image/heic','image/heif'];

// ─────────────────────────────────────────────────────────────────────────────
// ImageUploader
//
// Props:
//   value      – current URL string (controlled)
//   onChange   – called with new URL string after successful upload or manual paste
//   lang       – 'ar' | 'en'
//   nameHint   – used for the sanitized filename (e.g. product name or sku)
//   compact    – smaller layout, useful inside VariantManager rows
//   label      – optional override label shown above the zone
// ─────────────────────────────────────────────────────────────────────────────
export default function ImageUploader({
  value,
  onChange,
  lang       = 'en',
  nameHint   = 'product',
  compact    = false,
  label      = null,
}) {
  const ar = lang === 'ar';
  const { upload, status, progress, error, reset } = useImageUpload();

  const [dragging,  setDragging]  = useState(false);
  const [preview,   setPreview]   = useState(value || '');
  const [showUrl,   setShowUrl]   = useState(false);
  const fileInputRef              = useRef(null);

  // Keep preview in sync when external value changes (e.g. editing a saved product)
  useEffect(() => {
    if (value && status === 'idle') setPreview(value);
  }, [value, status]);

  // ── File selection handler ───────────────────────────────────────────────
  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (!ACCEPT.includes(file.type)) {
      alert(ar
        ? 'نوع الملف غير مدعوم. يُرجى اختيار صورة (JPEG/PNG/WebP/GIF).'
        : 'Unsupported file type. Please select an image (JPEG, PNG, WebP, or GIF).');
      return;
    }

    // Show local preview immediately — no waiting for upload
    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);

    const resultUrl = await upload(file, nameHint);

    // Always revoke the local object URL to free memory
    URL.revokeObjectURL(localUrl);

    if (resultUrl) {
      setPreview(resultUrl);
      onChange(resultUrl);
    } else {
      // Upload failed — restore previous value as preview
      setPreview(value || '');
    }
  }, [upload, onChange, nameHint, value, ar]);

  // ── Drag & Drop ──────────────────────────────────────────────────────────
  const onDragOver  = useCallback((e) => { e.preventDefault(); setDragging(true);  }, []);
  const onDragLeave = useCallback(()  => { setDragging(false); }, []);
  const onDrop      = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const onFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';   // reset so same file can be re-selected
  };

  const handleReset = (e) => {
    e.stopPropagation();
    reset();
    setPreview(value || '');
  };

  const isWorking = status === 'compressing' || status === 'uploading';
  const phase     = PHASE[status];
  const thumbSz   = compact ? 52 : 80;

  // ── Styles ───────────────────────────────────────────────────────────────
  const zoneStyle = {
    border:         `2px dashed ${dragging ? 'var(--sedra-forest-soft)' : isWorking ? 'var(--sedra-sage-ink)' : '#d4d0c8'}`,
    borderRadius:   10,
    padding:        compact ? '10px 12px' : '18px 16px',
    display:        'flex',
    alignItems:     'center',
    gap:            12,
    cursor:         isWorking ? 'default' : 'pointer',
    background:     dragging ? 'var(--sedra-sage-wash)' : 'var(--sedra-sage-wash)',
    transition:     'border-color 0.2s, background 0.2s',
    userSelect:     'none',
    direction:      ar ? 'rtl' : 'ltr',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 6 : 8 }}>

      {/* Optional label */}
      {label && (
        <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 2 }}>
          {label}
        </label>
      )}

      {/* ── Drop Zone ────────────────────────────────────────────────────── */}
      <div
        style={zoneStyle}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => !isWorking && fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && !isWorking && fileInputRef.current?.click()}
        aria-label={ar ? 'رفع صورة' : 'Upload image'}
      >
        {/* Thumbnail */}
        {preview ? (
          <img
            src={preview}
            alt="preview"
            style={{
              width: thumbSz, height: thumbSz,
              objectFit: 'cover', borderRadius: 8, flexShrink: 0,
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            }}
          />
        ) : (
          <div style={{
            width: thumbSz, height: thumbSz, flexShrink: 0,
            borderRadius: 8, background: 'var(--sedra-image-well)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#bbb',
          }}>
            <Icon name="Camera" size={compact ? 20 : 26} />
          </div>
        )}

        {/* Right-hand text + progress */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Phase label */}
          {status !== 'idle' && phase && (
            <div style={{
              fontSize: 12, fontWeight: 600,
              color: status === 'error' ? 'var(--status-danger)' : 'var(--sedra-forest-soft)',
              marginBottom: 4,
            }}>
              {ar ? phase.label_ar : phase.label_en}
            </div>
          )}

          {/* Idle prompt */}
          {status === 'idle' && (
            <div style={{ fontSize: compact ? 11 : 12, color: '#888', lineHeight: 1.5 }}>
              {preview
                ? (ar ? 'انقر أو اسحب صورة للاستبدال' : 'Click or drag to replace')
                : (ar ? 'انقر أو اسحب الصورة هنا' : 'Click or drag image here')}
              {!compact && (
                <div style={{ fontSize: 11, color: '#bbb', marginTop: 2 }}>
                  {ar
                    ? 'JPEG · PNG · WebP · GIF — بحد أقصى 10 ميغابايت'
                    : 'JPEG · PNG · WebP · GIF — max 10 MB'}
                  <br />
                  {ar
                    ? 'يتم ضغط الصورة وتحويلها إلى WebP تلقائياً'
                    : 'Auto-compressed & converted to WebP on upload'}
                </div>
              )}
            </div>
          )}

          {/* Progress bar */}
          {(isWorking || status === 'done') && (
            <div style={{
              height: 5, borderRadius: 4,
              background: 'var(--sedra-line)', overflow: 'hidden',
              marginTop: status === 'idle' ? 6 : 4,
            }}>
              <div style={{
                height: '100%', borderRadius: 4,
                background: phase?.bar || 'var(--sedra-forest-soft)',
                width: `${progress}%`,
                transition: 'width 0.25s ease',
              }} />
            </div>
          )}

          {/* Error message + reset */}
          {status === 'error' && (
            <div style={{ fontSize: 11, color: 'var(--status-danger)', marginTop: 4, lineHeight: 1.4 }}>
              {error}
              <button
                type="button"
                onClick={handleReset}
                style={{
                  marginInlineStart: 8, fontSize: 11, color: '#888',
                  background: 'none', border: 'none', cursor: 'pointer',
                  textDecoration: 'underline', padding: 0,
                }}>
                {ar ? 'إعادة المحاولة' : 'Try again'}
              </button>
            </div>
          )}

          {/* Done: tiny filesize note */}
          {status === 'done' && (
            <div style={{ fontSize: 11, color: 'var(--sedra-sage-ink)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Icon name="Check" size={11} /> {ar ? 'الصورة جاهزة' : 'Image ready'}
            </div>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={onFileInput}
        />
      </div>

      {/* ── Manual URL fallback (collapsible) ────────────────────────────── */}
      <div>
        <button
          type="button"
          onClick={() => setShowUrl(v => !v)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 11, color: '#aaa', padding: 0,
            textDecoration: 'underline', display: 'flex',
            alignItems: 'center', gap: 4,
          }}>
          <Icon name={showUrl ? 'ChevronDown' : 'Link'} size={11} style={{ transform: showUrl ? 'rotate(180deg)' : 'none' }} />
          {showUrl
            ? (ar ? 'إخفاء حقل الرابط اليدوي' : 'Hide manual URL')
            : (ar ? 'أو ألصق رابط صورة مباشرة…' : 'Or paste an image URL directly…')}
        </button>

        {showUrl && (
          <input
            type="text"
            value={value || ''}
            onChange={e => { onChange(e.target.value); setPreview(e.target.value); }}
            placeholder={ar ? 'https://رابط-الصورة.jpg' : 'https://example.com/image.jpg'}
            dir={ar ? 'ltr' : 'ltr'}   // URLs are always LTR
            style={{
              marginTop: 6, width: '100%', boxSizing: 'border-box',
              padding: '7px 10px', borderRadius: 7,
              border: '1.5px solid var(--sedra-line)', fontSize: 13,
              fontFamily: 'inherit',
              background: '#fff',
            }}
          />
        )}
      </div>
    </div>
  );
}
